import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { IOrganizationAccess } from "src/app/shared/interfaces/access.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { IAcceptInvitation, IInvitationDetails } from "../interfaces/invitation.interface";

@Injectable({
  providedIn: "root",
})
export class InvitationService {
  constructor(private api: ApiService) {}

  /**
   * Invite a new member to the organization.
   * @param email The new member's email address
   * @param role The role to give to the new member
   * @return The invitation details, including whether the given email is already in use
   */
  public inviteToOrg(email: string, role: string): Observable<IInvitationDetails> {
    return this.api.post("invitations", undefined, undefined, undefined, { email, role }) as Observable<IInvitationDetails>;
  }

  /**
   * Get invitation information from an invitation token.
   * @param token The invitation token received by email
   * @return The invitation details, including whether the given email is already in use
   */
  public getInvitationDetails(token: string): Observable<IInvitationDetails> {
    return this.api.get(`invitations/${encodeURIComponent(token)}`) as Observable<IInvitationDetails>;
  }

  /**
   * Accept an invitation using the invitation token.
   * @param token The invitation token received by email
   * @param invitation The supplementary invitation information
   * @param jwt The access token obtained by logging in with an already-existing account
   * @return Access and refresh tokens, along with the functionalities and routes the invitee's
   * role in the organization grants access to
   */
  public acceptInvitation(token: string, invitation: IAcceptInvitation, jwt?: string): Observable<IOrganizationAccess> {
    const headerParams = jwt ? { Authorization: `Bearer ${jwt}` } : undefined;

    return this.api.post(
      `invitations/${encodeURIComponent(token)}/accept`,
      invitation,
      headerParams,
      false
    ) as Observable<IOrganizationAccess>;
  }

  /**
   * Resend an invitation, generating a new token and expiration date.
   * @param id The id of the invitation to resend
   */
  public resendInvitation(id: number): Observable<void> {
    return this.api.post(`invitations/${encodeURIComponent(id)}/resend`, undefined) as Observable<void>;
  }

  /**
   * Revoke a pending invitation.
   * @param id The id of the invitation to revoke
   */
  public revokeInvitation(id: number): Observable<void> {
    return this.api.post(`invitations/${encodeURIComponent(id)}/revoke`, undefined) as Observable<void>;
  }
}
